import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Divider,
} from '@mui/material'
import { IconGift, IconPackage, IconSearch } from '@tabler/icons-react'
import { PricingType, Product } from '../../types'
import { channels } from '../../../../constants'

type ProductType = 'single' | 'bundle'

interface CreateProductDialogProps {
  open: boolean
  onClose: () => void
  onCreate: (data: ProductFormData) => void
  availableProducts: Product[]
}

export interface ProductFormData {
  productType: ProductType
  name: string
  bundleId: string
  supplier: string
  price: string
  duration: number
  status: 'active' | 'inactive'
  purchaseConditionType: PricingType
  purchaseConditionValue: string
  selectedProducts: string[]
}

const initialFormData: ProductFormData = {
  productType: 'single',
  name: '',
  bundleId: '',
  supplier: '',
  price: '',
  duration: 30,
  status: 'active',
  purchaseConditionType: 'fixPrice',
  purchaseConditionValue: '',
  selectedProducts: [],
}

const CreateProductDialog = ({
  open,
  onClose,
  onCreate,
  availableProducts,
}: CreateProductDialogProps) => {
  const [formData, setFormData] = useState<ProductFormData>(initialFormData)
  const [productSearch, setProductSearch] = useState('')

  const handleChange = (field: keyof ProductFormData, value: string | number | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleClose = () => {
    setFormData(initialFormData)
    setProductSearch('')
    onClose()
  }

  const handleCreate = () => {
    onCreate(formData)
    setFormData(initialFormData)
    setProductSearch('')
  }

  const handleToggleProduct = (productId: string) => {
    const currentSelected = formData.selectedProducts
    const newSelected = currentSelected.includes(productId)
      ? currentSelected.filter((id) => id !== productId)
      : [...currentSelected, productId]
    handleChange('selectedProducts', newSelected)
  }

  const filteredProducts = availableProducts.filter(
    (product) =>
      product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      (product.supplier ?? '').toLowerCase().includes(productSearch.toLowerCase())
  )

  const isSingleProduct = formData.productType === 'single'

  const isFormValid = isSingleProduct
    ? formData.name.trim() !== '' &&
      formData.supplier !== '' &&
      parseFloat(formData.price) > 0 &&
      formData.duration > 0 &&
      parseFloat(formData.purchaseConditionValue) > 0
    : formData.name.trim() !== '' &&
      parseFloat(formData.price) > 0 &&
      formData.duration > 0 &&
      parseFloat(formData.purchaseConditionValue) > 0 &&
      formData.selectedProducts.length > 0

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h4" color="primary" fontWeight={600}>
          Create New Product
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Product Type
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={formData.productType === 'single' ? 'contained' : 'outlined'}
                onClick={() => handleChange('productType', 'single')}
                startIcon={<IconGift size={18} />}
                sx={{ py: 1.5, borderRadius: '8px', textTransform: 'none' }}
              >
                Single Product
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={formData.productType === 'bundle' ? 'contained' : 'outlined'}
                onClick={() => handleChange('productType', 'bundle')}
                startIcon={<IconPackage size={18} />}
                sx={{ py: 1.5, borderRadius: '8px', textTransform: 'none' }}
              >
                Bundle
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
            {isSingleProduct ? 'Product Name' : 'Bundle Name'}
          </Typography>
          <TextField
            fullWidth
            placeholder={isSingleProduct ? 'e.g. Booster' : 'e.g. Social Media Starter'}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            size="small"
            sx={{ mb: 2 }}
          />

          {isSingleProduct && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Supplier
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.supplier}
                    onChange={(e) => handleChange('supplier', e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select supplier
                    </MenuItem>
                    {channels.map((channel) => (
                      <MenuItem key={channel.value} value={channel.value}>
                        {channel.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Sales Price (€)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g. 99.99"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
            </Grid>
          )}

          {isSingleProduct && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Duration (Days)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', parseInt(e.target.value, 10) || 0)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {!isSingleProduct && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Contained Products
              </Typography>
              <TextField
                fullWidth
                placeholder="Search products (Name, Supplier)..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                size="small"
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconSearch size={18} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  maxHeight: 200,
                  overflow: 'auto',
                  mb: 2,
                }}
              >
                <List dense disablePadding>
                  {filteredProducts.map((product) => (
                    <ListItem
                      key={product.id}
                      disablePadding
                      secondaryAction={
                        <Typography variant="body2" color="text.secondary">
                          {product.price
                            ? `${parseFloat(product.price).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                              })}€`
                            : '—'}
                        </Typography>
                      }
                    >
                      <ListItemButton onClick={() => handleToggleProduct(product.id)} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={formData.selectedProducts.includes(product.id)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              <strong>{product.name}</strong>{' '}
                              <Typography component="span" variant="body2" color="text.secondary">
                                {product.supplier ? `(${product.supplier})` : ''} [#
                                {product.displayId}]
                              </Typography>
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {!isSingleProduct && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Bundle Price (€)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g. 99.99"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Duration (Days)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', parseInt(e.target.value, 10) || 0)}
                  size="small"
                />
              </Grid>
            </Grid>
          )}

          {!isSingleProduct && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Status
              </Typography>
              <FormControl size="small" sx={{ mb: 3, minWidth: 200 }}>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 2 }}>
            Purchase Conditions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.purchaseConditionType}
                  onChange={(e) => handleChange('purchaseConditionType', e.target.value)}
                >
                  <MenuItem value="fixPrice">Fix Price</MenuItem>
                  <MenuItem value="percentage">Percentage</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Conditions
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder={formData.purchaseConditionType === 'fixPrice' ? '100.00' : '15'}
                value={formData.purchaseConditionValue}
                onChange={(e) => handleChange('purchaseConditionValue', e.target.value)}
                size="small"
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Discount on the sales price
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ px: 4, borderRadius: '8px', textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isFormValid}
          sx={{ px: 4, borderRadius: '8px', textTransform: 'none' }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateProductDialog
